/** @format */
import {useState} from 'react';

import {Modal} from '@mui/material';

import {BillingDiscountPeriod} from 'graphql/generated/types';

import {useApolloError} from 'helpers/hooks/useApolloError';

import {CardData, PartnerDiscount} from 'pages/Tariffs/types';

import {getDurationMonths} from 'pages/Tariffs/helpers/getDurationMonths';

import {AvailableBusinessTariff} from 'features/tariffs/types';

import {ModalPositionBox, Root} from './styles';
import {Card} from '../Card';
import {DetailedModalContent} from './modals/DetailedModalContent';
import {MessageResultModalContent} from './modals/MessageResultModalContent';
import {
  useCreateBillMutation,
  useRequestBankPaymentMutation,
  useRequestUltraTariffMutation,
} from './graphql/__generated__';

interface Props {
  duration: BillingDiscountPeriod;
  cardsData: CardData[];
  partnerDiscount: PartnerDiscount | null;
}

const Cards = ({duration, cardsData, partnerDiscount}: Props) => {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [loadingPayment, setLoadingPayment] = useState<boolean>(false);
  const [selectedTariffId, setSelectedTariffId] = useState<AvailableBusinessTariff | null>(null);

  const {onError, isError, renderAlert} = useApolloError();

  const [create, {loading: loadingCreate}] = useCreateBillMutation();
  const [requestUltra, {loading: loadingUltra}] = useRequestUltraTariffMutation();
  const [requestBankPayment, {loading: loadingBank}] = useRequestBankPaymentMutation();

  const loading = loadingCreate || loadingUltra || loadingBank || loadingPayment;

  const handleSelect = async (id: AvailableBusinessTariff, isPrice: boolean) => {
    setSelectedTariffId(id);
    if (isPrice) {
      setIsOpenModal(true);
    } else {
      try {
        const response = await requestUltra({
          variables: {
            months: getDurationMonths(duration),
          },
        });
        if (response.data?.requestUltraTariff) {
          setSelectedTariffId(null);
          setIsOpenModal(true);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        onError();
      }
    }
  };

  const handleCardPay = async () => {
    if (!selectedTariffId) {
      return;
    }

    try {
      const response = await create({
        variables: {
          tariffCode: selectedTariffId,
          months: getDurationMonths(duration),
        },
      });
      const billId = response.data?.createBill.id;
      if (billId) {
        setLoadingPayment(true);
        const url = `${import.meta.env.VITE_YALPER_URI}/bills/${billId}/make_payment`;
        location.assign(url);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      onError();
    }
  };

  const handleBillPay = async () => {
    try {
      if (!selectedTariffId) {
        return;
      }
      const response = await requestBankPayment({
        variables: {
          tariffCode: selectedTariffId,
          months: getDurationMonths(duration),
        },
      });
      if (response.data?.requestBankPayment) {
        setSelectedTariffId(null);
        setIsOpenModal(true);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      onError();
    }
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
    setSelectedTariffId(null);
  };

  const prices = cardsData.find(card => card.id === selectedTariffId)?.prices;
  const price = prices?.find(v => v.period === duration);
  const isTariffWithPrice = typeof price?.amount === 'number';

  return (
    <Root>
      {cardsData.map(tariff => (
        <Card
          key={tariff.id}
          cardData={tariff}
          onSelect={handleSelect}
          duration={duration}
          loading={loading}
          selectedTariffId={selectedTariffId}
        />
      ))}

      <Modal open={isOpenModal} onClose={handleCloseModal}>
        <ModalPositionBox>
          {price && isTariffWithPrice ? (
            <DetailedModalContent
              price={price}
              duration={duration}
              partnerDiscount={partnerDiscount}
              onCardPay={handleCardPay}
              onBillPay={handleBillPay}
              disabled={loading || isError}
            />
          ) : (
            <MessageResultModalContent onClose={handleCloseModal} />
          )}
          {renderAlert}
        </ModalPositionBox>
      </Modal>
    </Root>
  );
};

export {Cards};
